"use client";

import { useState, useEffect, useCallback } from "react";
import {
  ontologyDiscoverForeignConnectionSchemas,
  ontologyDiscoverForeignConnectionTables,
  ontologyGetForeignTableDetails,
} from "../sdk/sdk.gen";
import type {
  ListForeignSchemasResponse,
  ListForeignTablesResponse,
  ForeignTableDetailsResponse,
  ForeignSchemaInfo,
  ForeignTableInfo,
} from "../sdk/types.gen";

/**
 * Hook for discovering schemas in a foreign connection
 *
 * @param connectionName - Foreign connection name
 * @param orgId - Organization ID
 * @returns Schemas and discovery operations
 */
export function useForeignConnectionSchemas(
  connectionName?: string | null,
  orgId?: string
) {
  const [schemas, setSchemas] = useState<ForeignSchemaInfo[]>([]);
  const [totalSchemas, setTotalSchemas] = useState(0);
  const [totalTables, setTotalTables] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const discoverSchemas = useCallback(async () => {
    if (!connectionName || !orgId) {
      setSchemas([]);
      setTotalSchemas(0);
      setTotalTables(0);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      console.log("🔵 Discovering foreign connection schemas:", {
        connectionName,
        orgId,
        timestamp: new Date().toISOString(),
      });

      const response = await ontologyDiscoverForeignConnectionSchemas({
        path: { connection_name: connectionName },
        query: { org_id: orgId },
      });

      console.log("🟢 Schemas discovered successfully:", response.data);

      setSchemas(response.data?.schemas || []);
      setTotalSchemas(response.data?.total_schemas || 0);
      setTotalTables(response.data?.total_tables || 0);
    } catch (err) {
      console.error("🔴 Error discovering schemas:", {
        error: err,
        message: err instanceof Error ? err.message : "Unknown error",
        connectionName,
        orgId,
      });

      const errorMessage =
        err instanceof Error
          ? err.message
          : "Failed to discover foreign connection schemas";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [connectionName, orgId]);

  useEffect(() => {
    if (connectionName && orgId) {
      discoverSchemas();
    }
  }, [discoverSchemas, connectionName, orgId]);

  return {
    schemas,
    totalSchemas,
    totalTables,
    loading,
    error,
    refetch: discoverSchemas,
  };
}

/**
 * Hook for discovering tables in a foreign connection
 *
 * @param connectionName - Foreign connection name
 * @param orgId - Organization ID
 * @param schemaName - Optional schema filter
 * @returns Tables and discovery operations
 */
export function useForeignConnectionTables(
  connectionName?: string | null,
  orgId?: string,
  schemaName?: string | null
) {
  const [tables, setTables] = useState<ForeignTableInfo[]>([]);
  const [totalTables, setTotalTables] = useState(0);
  const [discoveredAt, setDiscoveredAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const discoverTables = useCallback(async () => {
    if (!connectionName || !orgId) {
      setTables([]);
      setTotalTables(0);
      setDiscoveredAt(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      console.log("🔵 Discovering foreign connection tables:", {
        connectionName,
        orgId,
        schemaName,
        timestamp: new Date().toISOString(),
      });

      const response = await ontologyDiscoverForeignConnectionTables({
        path: { connection_name: connectionName },
        query: {
          org_id: orgId,
          ...(schemaName && { schema_name: schemaName }),
        },
      });

      console.log("🟢 Tables discovered successfully:", response.data);

      setTables(response.data?.tables || []);
      setTotalTables(response.data?.total_tables || 0);
      setDiscoveredAt(response.data?.discovered_at || null);
    } catch (err) {
      console.error("🔴 Error discovering tables:", {
        error: err,
        message: err instanceof Error ? err.message : "Unknown error",
        connectionName,
        orgId,
        schemaName,
      });

      const errorMessage =
        err instanceof Error
          ? err.message
          : "Failed to discover foreign connection tables";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [connectionName, orgId, schemaName]);

  useEffect(() => {
    if (connectionName && orgId) {
      discoverTables();
    }
  }, [discoverTables, connectionName, orgId, schemaName]);

  return {
    tables,
    totalTables,
    discoveredAt,
    loading,
    error,
    refetch: discoverTables,
  };
}

/**
 * Hook for getting detailed information about a foreign table
 *
 * @param connectionName - Foreign connection name
 * @param schemaName - Schema name
 * @param tableName - Table name
 * @param orgId - Organization ID
 * @returns Table details and operations
 */
export function useForeignTableDetails(
  connectionName?: string | null,
  schemaName?: string | null,
  tableName?: string | null,
  orgId?: string
) {
  const [tableDetails, setTableDetails] =
    useState<ForeignTableDetailsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getTableDetails = useCallback(async () => {
    if (!connectionName || !schemaName || !tableName || !orgId) {
      setTableDetails(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      console.log("🔵 Getting foreign table details:", {
        connectionName,
        schemaName,
        tableName,
        orgId,
        timestamp: new Date().toISOString(),
      });

      const response = await ontologyGetForeignTableDetails({
        path: {
          connection_name: connectionName,
          schema_name: schemaName,
          table_name: tableName,
        },
        query: { org_id: orgId },
      });

      console.log("🟢 Table details retrieved successfully:", response.data);

      setTableDetails(response.data ?? null);
    } catch (err) {
      console.error("🔴 Error getting table details:", {
        error: err,
        message: err instanceof Error ? err.message : "Unknown error",
        connectionName,
        schemaName,
        tableName,
        orgId,
      });

      const errorMessage =
        err instanceof Error
          ? err.message
          : "Failed to get foreign table details";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [connectionName, schemaName, tableName, orgId]);

  useEffect(() => {
    if (connectionName && schemaName && tableName && orgId) {
      getTableDetails();
    }
  }, [getTableDetails, connectionName, schemaName, tableName, orgId]);

  return {
    tableDetails,
    loading,
    error,
    refetch: getTableDetails,
  };
}
